import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Box,
    CircularProgress,
    Collapse,
    List,
    ListItemButton,
    Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
    CatalogRelationships,
    CatalogRelationshipsAsset,
    CatalogRelationshipsSeason,
} from "./availsPlusDetailTypes";
import {
    AVAILS_DETAIL_DRAWER_COLORS,
    detailDrawerHierarchyCurrentSx,
    detailDrawerHierarchyLinkSx,
    detailDrawerHierarchyMetaSx,
    detailDrawerLabelSx,
    detailDrawerSectionTitleSx,
    detailDrawerValueSx,
} from "./availsPlusDetailStyles";

type HierarchyLayout = "series" | "movie";

/** Theme spacing units — keep shallow so nested rows stay aligned. */
const INDENT = {
    series: 1,
    asset: 2.75,
} as const;

const assetRowBaseSx = {
    py: 0.4,
    pl: INDENT.asset,
    pr: 0.75,
    borderRadius: 1,
    textAlign: "left" as const,
    width: "100%",
};

function HierarchyMetaRow({
    label,
    value,
    indent = 0,
    mono = false,
}: {
    label: string;
    value: string;
    indent?: number;
    mono?: boolean;
}) {
    return (
        <Box sx={{ pl: indent, mb: 0.75 }}>
            <Typography variant="caption" display="block" sx={{ mb: 0.15, ...detailDrawerLabelSx }}>
                {label}
            </Typography>
            <Typography
                variant="body2"
                title={value}
                sx={{
                    wordBreak: "break-word",
                    lineHeight: 1.35,
                    ...(mono ? detailDrawerHierarchyMetaSx : detailDrawerValueSx),
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function looksLikeRefId(value: string): boolean {
    const trimmed = value.trim();
    return trimmed.length > 12 && /^[A-Z0-9_]+$/i.test(trimmed) && trimmed.includes("_");
}

function isTechnicalSeasonTitle(title: string, season: CatalogRelationshipsSeason): boolean {
    const normalized = title.trim();
    if (!normalized) return true;
    if (normalized === season.season_ref_id?.trim()) return true;
    if (/^[\w-]+,\s*S\d+$/i.test(normalized)) return true;
    return looksLikeRefId(normalized);
}

function getAssetTypeLabel(asset: CatalogRelationshipsAsset, layout: HierarchyLayout): string {
    const type = asset.content_type?.toLowerCase();
    if (type === "movie") return "Movie";
    if (type === "supplemental") {
        const title = asset.title?.toLowerCase() ?? "";
        if (title.includes("trailer")) {
            return layout === "movie" ? "Trailer" : "Trailer";
        }
        return "Extra";
    }
    if (type === "episode") {
        return asset.episode_number != null ? `Ep ${asset.episode_number}` : "Episode";
    }
    return type || "Asset";
}

function resolveHierarchyLayout(
    relationships: CatalogRelationships,
    contentType?: string
): HierarchyLayout {
    const type = contentType?.toLowerCase();
    if (type === "movie") return "movie";
    if (relationships.seasons?.length || relationships.season_ref_id) return "series";
    return "movie";
}

function dedupeAssets(assets: CatalogRelationshipsAsset[]): CatalogRelationshipsAsset[] {
    const map = new Map<string, CatalogRelationshipsAsset>();
    for (const asset of assets) {
        map.set(asset.ref_id, asset);
    }
    return [...map.values()];
}

function pickBetterSeasonTitle(
    a: CatalogRelationshipsSeason,
    b: CatalogRelationshipsSeason
): string {
    const aTitle = a.season_title?.trim() || "";
    const bTitle = b.season_title?.trim() || "";
    if (aTitle && !isTechnicalSeasonTitle(aTitle, a)) return aTitle;
    if (bTitle && !isTechnicalSeasonTitle(bTitle, b)) return bTitle;
    return aTitle || bTitle;
}

function normalizeSeasonTitle(title?: string): string {
    return String(title || "")
        .trim()
        .toLowerCase()
        .replace(/,\s*s(\d+)$/i, " s$1")
        .replace(/\s+/g, " ");
}

function dedupeSeasons(seasons: CatalogRelationshipsSeason[]): CatalogRelationshipsSeason[] {
    const map = new Map<string, CatalogRelationshipsSeason>();

    const resolveKey = (season: CatalogRelationshipsSeason): string => {
        if (season.season_number != null) return `n:${season.season_number}`;
        if (season.season_ref_id?.trim()) return `ref:${season.season_ref_id.trim()}`;
        return `title:${normalizeSeasonTitle(season.season_title) || "unknown"}`;
    };

    const findMergeTarget = (
        season: CatalogRelationshipsSeason
    ): CatalogRelationshipsSeason | undefined => {
        const preferredKey = resolveKey(season);
        const byPreferred = map.get(preferredKey);
        if (byPreferred) return byPreferred;

        const seasonRef = season.season_ref_id?.trim();
        if (seasonRef) {
            for (const existing of map.values()) {
                if (existing.season_ref_id?.trim() === seasonRef) return existing;
            }
        }

        if (season.season_number != null) {
            for (const existing of map.values()) {
                if (existing.season_number === season.season_number) return existing;
            }
        }

        const seasonTitle = normalizeSeasonTitle(season.season_title);
        if (seasonTitle) {
            for (const existing of map.values()) {
                if (normalizeSeasonTitle(existing.season_title) === seasonTitle) {
                    return existing;
                }
            }
        }

        return undefined;
    };

    for (const season of seasons) {
        const existing = findMergeTarget(season);
        if (!existing) {
            map.set(resolveKey(season), season);
            continue;
        }
        const merged: CatalogRelationshipsSeason = {
            ...existing,
            season_ref_id: existing.season_ref_id || season.season_ref_id,
            season_number: existing.season_number ?? season.season_number,
            season_title: pickBetterSeasonTitle(existing, season),
            assets: dedupeAssets([...existing.assets, ...season.assets]),
        };
        for (const [key, value] of map.entries()) {
            if (value === existing) map.delete(key);
        }
        map.set(resolveKey(merged), merged);
    }

    return [...map.values()];
}

function sortSeasonAssets(assets: CatalogRelationshipsAsset[]): CatalogRelationshipsAsset[] {
    const isSupplemental = (asset: CatalogRelationshipsAsset) =>
        asset.content_type?.toLowerCase() === "supplemental";

    return [...assets].sort((a, b) => {
        const aSupplemental = isSupplemental(a);
        const bSupplemental = isSupplemental(b);
        if (aSupplemental !== bSupplemental) return aSupplemental ? -1 : 1;

        const aEpisodeNum = a.episode_number ?? Number.MAX_SAFE_INTEGER;
        const bEpisodeNum = b.episode_number ?? Number.MAX_SAFE_INTEGER;
        if (aEpisodeNum !== bEpisodeNum) return aEpisodeNum - bEpisodeNum;

        return String(a.title || "").localeCompare(String(b.title || ""));
    });
}

function resolveSeasons(
    relationships: CatalogRelationships,
    currentRefId?: string,
    currentTitle?: string,
    episodeNumber?: number | null
): CatalogRelationshipsSeason[] {
    const sortSeasons = (items: CatalogRelationshipsSeason[]) =>
        dedupeSeasons(items).sort((a, b) => {
            const aNum = a.season_number ?? Number.MAX_SAFE_INTEGER;
            const bNum = b.season_number ?? Number.MAX_SAFE_INTEGER;
            if (aNum !== bNum) return aNum - bNum;
            return String(a.season_ref_id || "").localeCompare(String(b.season_ref_id || ""));
        });

    if (relationships.seasons?.length) {
        return sortSeasons(
            relationships.seasons.map((season) => ({
                ...season,
                assets: sortSeasonAssets(season.assets),
            }))
        );
    }
    if (relationships.assets.length > 0 && relationships.season_ref_id) {
        return sortSeasons([
            {
                season_ref_id: relationships.season_ref_id,
                season_number: relationships.season_number,
                season_title: relationships.season_title,
                assets: sortSeasonAssets(relationships.assets),
            },
        ]);
    }
    if (relationships.season_ref_id && currentRefId) {
        return sortSeasons([
            {
                season_ref_id: relationships.season_ref_id,
                season_number: relationships.season_number,
                season_title: relationships.season_title || "",
                assets: sortSeasonAssets([
                    {
                        ref_id: currentRefId,
                        content_type: "episode",
                        title: currentTitle || currentRefId,
                        episode_number: episodeNumber ?? null,
                        season_number: relationships.season_number ?? null,
                        clickable: false,
                    },
                ]),
            },
        ]);
    }
    return [];
}

function resolveSeasonLabel(season: CatalogRelationshipsSeason): string {
    const title = season.season_title?.trim();
    if (title && !isTechnicalSeasonTitle(title, season)) return title;
    if (season.season_number != null) return `Season ${season.season_number}`;
    if (title) return title;
    if (season.season_ref_id?.trim()) return season.season_ref_id.trim();
    return "Season";
}

function resolveSeriesLabel(relationships: CatalogRelationships): string {
    const title = relationships.series_title?.trim();
    if (title && !looksLikeRefId(title)) return title;
    if (title) return title;
    if (relationships.series_ref_id?.trim()) return relationships.series_ref_id.trim();
    return "";
}

function seasonKey(season: CatalogRelationshipsSeason): string {
    if (season.season_number != null) return `season-${season.season_number}`;
    return season.season_ref_id || `season-${season.season_title || "unknown"}`;
}

function renderAssetRow(
    asset: CatalogRelationshipsAsset,
    currentRefId: string,
    onNavigateRef: (refId: string) => void,
    layout: HierarchyLayout
) {
    const isCurrent = asset.ref_id === currentRefId;
    const canNavigate = asset.clickable !== false && !isCurrent;
    const typeLabel = getAssetTypeLabel(asset, layout);

    return (
        <Box
            key={asset.ref_id}
            aria-current={isCurrent ? "true" : undefined}
            onClick={canNavigate ? () => onNavigateRef(asset.ref_id) : undefined}
            sx={{
                ...assetRowBaseSx,
                display: "flex",
                gap: 0.75,
                alignItems: "flex-start",
                ...(isCurrent ? { bgcolor: "rgba(255, 255, 255, 0.1)" } : {}),
                ...(canNavigate
                    ? {
                          cursor: "pointer",
                          "&:hover": { bgcolor: "rgba(255, 255, 255, 0.06)" },
                      }
                    : {}),
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    flexShrink: 0,
                    minWidth: 44,
                    pt: 0.15,
                    color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
                    textTransform: "capitalize",
                }}
            >
                {typeLabel}
            </Typography>
            <Typography
                variant="body2"
                title={asset.title}
                sx={{
                    minWidth: 0,
                    wordBreak: "break-word",
                    lineHeight: 1.35,
                    ...(isCurrent
                        ? { ...detailDrawerHierarchyCurrentSx, fontWeight: 700 }
                        : canNavigate
                          ? detailDrawerHierarchyLinkSx
                          : detailDrawerValueSx),
                }}
            >
                {asset.title}
            </Typography>
        </Box>
    );
}

function SeasonHierarchyBlock({
    season,
    currentRefId,
    onNavigateRef,
    layout,
    defaultExpanded,
}: {
    season: CatalogRelationshipsSeason;
    currentRefId: string;
    onNavigateRef: (refId: string) => void;
    layout: HierarchyLayout;
    defaultExpanded: boolean;
}) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const seasonLabel = resolveSeasonLabel(season);
    const containsCurrent = season.assets.some((asset) => asset.ref_id === currentRefId);

    return (
        <Box sx={{ mb: 0.25 }}>
            <ListItemButton
                onClick={() => setExpanded((open) => !open)}
                sx={{
                    py: 0.45,
                    pl: 0.25,
                    pr: 0.75,
                    borderRadius: 1,
                    minHeight: 0,
                    color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
                    justifyContent: "flex-start",
                    gap: 0.5,
                    ...(containsCurrent && !expanded ? { bgcolor: "rgba(255, 255, 255, 0.04)" } : {}),
                    "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.06)",
                    },
                }}
            >
                {expanded ? (
                    <ExpandLessIcon sx={{ fontSize: 16, flexShrink: 0, opacity: 0.85 }} />
                ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16, flexShrink: 0, opacity: 0.85 }} />
                )}
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: containsCurrent ? 700 : 600,
                        lineHeight: 1.35,
                        ...detailDrawerValueSx,
                    }}
                >
                    {seasonLabel}
                </Typography>
            </ListItemButton>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pb: 0.25 }}>
                    {season.assets.map((asset) =>
                        renderAssetRow(asset, currentRefId, onNavigateRef, layout)
                    )}
                </List>
            </Collapse>
        </Box>
    );
}

interface HierarchySidebarProps {
    relationships: CatalogRelationships | null;
    loading: boolean;
    currentRefId: string;
    currentTitle?: string;
    episodeNumber?: number | null;
    contentType?: string;
    onNavigateRef: (refId: string) => void;
}

export default function HierarchySidebar({
    relationships,
    loading,
    currentRefId,
    currentTitle,
    episodeNumber,
    contentType,
    onNavigateRef,
}: HierarchySidebarProps) {
    const layout = relationships ? resolveHierarchyLayout(relationships, contentType) : "movie";
    const seasons = useMemo(
        () =>
            relationships
                ? resolveSeasons(relationships, currentRefId, currentTitle, episodeNumber)
                : [],
        [relationships, currentRefId, currentTitle, episodeNumber]
    );

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (!relationships) {
        return (
            <Typography variant="body2" sx={{ px: 1, ...detailDrawerLabelSx }}>
                No hierarchy available for this title.
            </Typography>
        );
    }

    const seriesLabel = resolveSeriesLabel(relationships);
    const movieAssets =
        layout === "movie" && seasons.length === 0
            ? sortSeasonAssets(relationships.assets)
            : [];
    const contentTypeLower = contentType?.toLowerCase();
    const familyRef =
        contentTypeLower === "episode" || contentTypeLower === "supplemental"
            ? relationships.series_ref_id?.trim() || currentRefId
            : currentRefId;
    const hasTreeContent =
        (layout === "series" && (seriesLabel || seasons.length > 0)) || movieAssets.length > 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                <AccountTreeIcon fontSize="small" sx={{ color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary }} />
                <Typography variant="subtitle2" sx={detailDrawerSectionTitleSx}>
                    Hierarchy
                </Typography>
            </Box>

            <Box
                sx={{
                    border: 1,
                    borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
                    borderRadius: 1,
                    bgcolor: "rgba(255, 255, 255, 0.02)",
                    px: 1.25,
                    py: 1.25,
                    ...detailDrawerValueSx,
                }}
            >
                <HierarchyMetaRow label="Family ref" value={familyRef} mono />

                {layout === "movie" && seriesLabel ? (
                    <HierarchyMetaRow
                        label="Movie"
                        value={seriesLabel}
                        indent={INDENT.series}
                        mono={looksLikeRefId(seriesLabel)}
                    />
                ) : null}

                {layout === "series" && seriesLabel ? (
                    <HierarchyMetaRow
                        label="Series"
                        value={seriesLabel}
                        indent={INDENT.series}
                        mono={looksLikeRefId(seriesLabel)}
                    />
                ) : null}

                {layout === "series" ? (
                    <Box sx={{ mt: 0.5, pl: INDENT.series }}>
                        {seasons.map((season) => (
                            <SeasonHierarchyBlock
                                key={seasonKey(season)}
                                season={season}
                                currentRefId={currentRefId}
                                onNavigateRef={onNavigateRef}
                                layout={layout}
                                defaultExpanded={season.assets.some(
                                    (asset) => asset.ref_id === currentRefId
                                )}
                            />
                        ))}
                    </Box>
                ) : null}

                {movieAssets.length > 0 ? (
                    <List disablePadding sx={{ mt: 0.5, pl: INDENT.series }}>
                        {movieAssets.map((asset) =>
                            renderAssetRow(asset, currentRefId, onNavigateRef, layout)
                        )}
                    </List>
                ) : null}

                {!hasTreeContent ? (
                    <Typography variant="body2" sx={{ mt: 0.5, ...detailDrawerLabelSx }}>
                        No related titles in this hierarchy.
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
